<script lang="ts" setup>
import { Tooltip, Interaction } from "chart.js";
import {
	computed,
	onBeforeMount,
	onBeforeUnmount,
	ref,
	toRefs,
	watch,
	watchEffect,
	inject,
	withDefaults,
} from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import {
	AnnotationBubblesPluginOptions,
	horizontalDateGranularity,
	horizontalSupportsAnnotationBubbles,
	setTooltipPosition,
	setCollisionToNearestArea,
} from "chartjs-graph";
import { useBaseStore } from "@/stores/base";
import { addWatcherForRealtime } from "@/modules/explorer/composables/realtime-watcher";
import {
	derivedDocumentKey,
	documentKey,
	graphSettingsKey,
} from "@/modules/explorer/ExplorerUtils";
import { DownloadGraphKey } from "@/modules/explorer/components/Graphs/store";
import GraphWorkerCanvas from "@/modules/explorer/components/Graphs/GraphWorkerCanvas.vue";
import { ApiError, Result } from "@/api/base";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { useApiClient } from "@/api/client";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import { GRAPH_DISPLAY_ONLY_FIELDS } from "@/modules/explorer/composables/graphDisplaySettings";

const explorerClient = useApiClient("explorer");
const dashboardClient = useApiClient("dashboard");
const hierarchyStore = useHierarchyStore();

const props = withDefaults(
	defineProps<{
		lastUpdated: number;
		isExplorer: boolean;
		isRenderingForGotenberg?: boolean;
		downloadKey?: DownloadGraphKey;
		fetchDataForGraph: () => Promise<Result<any, ApiError>>;
		isTooltipDisabled?: boolean;
		needsDataRefresh?: () => boolean;
		logDisplayOnlyChange?: () => void;
	}>(),
	{
		isExplorer: false,
		isRenderingForGotenberg: false,
		isTooltipDisabled: false,
	}
);

const { lastUpdated, isExplorer, isRenderingForGotenberg, isTooltipDisabled } =
	toRefs(props);

const derivedDocument = inject(derivedDocumentKey);
const document = inject(documentKey);
const graphSettings = inject(graphSettingsKey);

const emit = defineEmits(["update:isLoading", "update:derivedDocument"]);
const explorerStore = useExplorerStore();

const isDarkMode = computed(() => {
	return useBaseStore().appTheme === "DARK_BLUE_THEME";
});

// #fencework, re-render graph (in correct height) when hierarchies ( for navigation ) toggled
watch(explorerStore.explorerState.openHierarchiesInExplorer, (v) => {
	if (v.length > 0) {
		visible.value = false;
		setTimeout(() => {
			visible.value = true;
		});
	}
});

Tooltip.positioners.cursor = setTooltipPosition;
Interaction.modes.nearestArea = setCollisionToNearestArea;

const apiData = ref();
const unableToLoadData = ref(false);
const noDataForSelection = ref(false);
const tooMuchDataForSelection = ref(false);
const visible = ref(false);
const isLoading = ref(false);
addWatcherForRealtime(() => fetchDataForDocument(true));

const annotationBubblesOptions = computed(
	(): AnnotationBubblesPluginOptions | false => {
		if (!horizontalSupportsAnnotationBubbles(derivedDocument.value)) {
			return false;
		}
		return {
			scaleId: "x",
			granularity: horizontalDateGranularity(derivedDocument.value),
			annotations: explorerStore.explorerState.annotations,
		};
	}
);

const resolutionPlugin: Plugin = {
	id: "resolutionPlugin",
	afterLayout(chart) {
		currentGraphResolution.value = {
			w: chart.width,
			h: chart.height,
		};
	},
};

const setHierarchyNodes = async (hierarchyDefinitionIds: number[]) => {
	for (const id of hierarchyDefinitionIds) {
		if (id) {
			await hierarchyStore.getOrLoadHierarchy(
				{ id, name_style: derivedDocument.value.hierarchy_naming_style },
				async () => {
					const response = isExplorer.value
						? await explorerClient.getHierarchy(explorerStore.document, id)
						: await dashboardClient.getHierarchy(null, id);
					const nodes = response.value as unknown as QuickFilterNodeDTO[];
					return response.isOk() && nodes.length > 0 ? nodes[0] : null;
				}
			);
		}
	}
};

watch(
	() => derivedDocument.value?.active_filters,
	async (filters) => {
		const ids = filters
			?.map((f) => f.dimension.hierarchy_definition_id)
			?.filter((id) => id !== null);

		if (ids) {
			await setHierarchyNodes(ids);
		}
	},
	{ immediate: true }
);

const downloadGraphState = props.downloadKey
	? inject(props.downloadKey)
	: undefined;

const downloadProperties = computed(() => downloadGraphState?.properties);

const startDownload = computed(() => downloadGraphState?.startDownload?.value);

const workerCanvasRef = ref<InstanceType<typeof GraphWorkerCanvas> | null>(
	null
);
const currentGraphResolution = ref<{ w: number; h: number } | undefined>();

// watchEffect tracks every reactive property it reads, so toggling showTitle,
// changing resolution, backgroundColor, etc. all automatically re-trigger the
// export without needing a deep watch or manual dependency listing.
watchEffect(() => {
	if (!startDownload.value) return;
	if (!workerCanvasRef.value || !downloadGraphState) return;
	const p = downloadProperties.value?.value;
	if (!p) return;
	workerCanvasRef.value.triggerExport({
		width: p.resolution?.w ?? currentGraphResolution.value?.w ?? 800,
		height: p.resolution?.h ?? currentGraphResolution.value?.h ?? 600,
		backgroundColor: p.backgroundColor ?? "#ffffff",
		showTitle: p.showTitle,
		showSnapshotDate: p.showSnapshotDate,
		showChartConfig: p.showChartConfig,
		graphTitle: downloadGraphState.graphTitle.value ?? "",
	});
});

const applyDisplayOnlyChange = (): void => {
	const current = document?.value;
	if (!current || !derivedDocument?.value) {
		void fetchDataForDocument();
		return;
	}

	const updated = { ...derivedDocument.value } as Record<string, unknown>;
	const source = current as unknown as Record<string, unknown>;
	for (const field of GRAPH_DISPLAY_ONLY_FIELDS) {
		updated[field] = source[field];
	}

	emit("update:derivedDocument", updated);
	props.logDisplayOnlyChange?.();
};

watch(lastUpdated, () => {
	if (props.needsDataRefresh && !props.needsDataRefresh()) {
		applyDisplayOnlyChange();
		return;
	}
	fetchDataForDocument();
});
watch(isLoading, (newValue) => emit("update:isLoading", newValue));
watch(apiData, () => {
	// Once data arrives for the first render, make the canvas visible.
	// Subsequent updates are handled in-place by the worker.
	if (!visible.value) visible.value = true;
});

const fetchDataForDocument = async (inBackground = false) => {
	// Don't trigger a background update when a foreground update is happening.
	if (isLoading.value && inBackground) {
		return;
	}

	explorerStore.updateLoadingState(true, inBackground, isLoading);

	const response = await props.fetchDataForGraph();

	// Use helper variable because otherwise tooMuchDataForSelection will never become false after being true.
	// We can't set it to false immediately as then it will freeze when showing the previous response.
	let tooMuchData: boolean;
	if (response.isOk()) {
		if (response.value?.datasets?.length) {
			/**
			 * Maximum number of data points / bars to render in graph
			 * (datasets × data points per dataset).
			 *
			 * Datasets influence this more than data points.
			 *
			 * At around 77,600 bars, things start to artifact and go crazy:
			 * ```
			 * X => subjectdateday
			 * XOffset => Week
			 * Legend => Flight - Departure Flight Code Share
			 * ```
			 *
			 * ---
			 *
			 * Around 300k bars is possible before the backend calls it quit.
			 * Though even then, it takes quite a while to render, and the tooltip becomes unusable
			 * ```
			 * x => subjectdateday
			 * Legend => carpark hierarchy (depth=3)
			 * Subject from/to => 2021/01/01 - 2027/12/31
			 * ```
			 *
			 * Schiphol would like to make some of these views, so the limit has been upped to 500k
			 */
			const MAX_DATA_POINTS = 500_000;

			if (
				response.value.datasets.length *
					4 *
					response.value.datasets[0].data.length >
				MAX_DATA_POINTS
			) {
				tooMuchData = true;
			}
		}

		if (!tooMuchData) {
			unableToLoadData.value = false;
			apiData.value = response.value;
			noDataForSelection.value = (response.value?.datasets?.length ?? []) === 0;
		}

		tooMuchDataForSelection.value = tooMuchData;
		explorerStore.updateLoadingState(false, inBackground, isLoading);
	}

	if (response.isErr() && response?.error?.status) {
		// Unable to process because it is too much to render
		if (response.error.status === 422) {
			noDataForSelection.value = true;
		} else {
			unableToLoadData.value = true;
		}
		explorerStore.updateLoadingState(false, inBackground, isLoading);
	}

	if (!response.isCanceled()) {
		explorerStore.explorerState.lastClickhouseQueryPerformance =
			response.value?.graphMeta?.performanceStatistics;
		explorerStore.explorerState.lastClickhouseSql =
			response.value?.graphMeta?.clickhouseSql;
		explorerStore.explorerState.lastClickhouseBaseDataSql =
			response.value?.graphMeta?.clickhouseBaseDataSql;
		explorerStore.explorerState.lastUsedMeasureDateTime =
			response.value?.usedMeasureDatetime;
		explorerStore.explorerState.lastLegendDimensionValuesIds =
			response.value?.graphMeta?.legendDimensionValuesIds;
		emit("update:derivedDocument", response.value?.derivedDocument);
	}
};

const registerCtrlPressed = (event) => {
	if (event.key === "Shift") {
		explorerStore.explorerState.isShiftPressed = true;
	}
};
const registerCtrlReleased = (event) => {
	if (event.key === "Shift") {
		explorerStore.explorerState.isShiftPressed = false;
	}
};

onBeforeMount(async () => {
	window.addEventListener("keydown", registerCtrlPressed);
	window.addEventListener("keyup", registerCtrlReleased);
	await fetchDataForDocument();
});

onBeforeUnmount(() => {
	window.removeEventListener("keydown", registerCtrlPressed);
	window.removeEventListener("keyup", registerCtrlReleased);
});
</script>

<template>
	<div class="align-center h-100 justify-center position-canvas-absolute">
		<GraphWorkerCanvas
			v-if="
				visible &&
				derivedDocument &&
				!unableToLoadData &&
				!noDataForSelection &&
				!tooMuchDataForSelection
			"
			ref="workerCanvasRef"
			:api-data="apiData"
			:derived-document="derivedDocument"
			:is-dark-mode="isDarkMode"
			:is-explorer="isExplorer"
			:is-rendering-for-gotenberg="isRenderingForGotenberg"
			:graph-settings="graphSettings ? graphSettings : null"
			:hierarchy-nodes="{ ...hierarchyStore.staticHierarchyNodes }"
			:annotation-bubbles-options="annotationBubblesOptions"
			:is-tooltip-disabled="isTooltipDisabled"
			@resolution="(r) => (currentGraphResolution = r)"
			@blob="
				(blob) => {
					if (downloadGraphState) downloadGraphState.graphImage.value = blob;
				}
			"
		/>

		<v-empty-state
			v-if="!isLoading && noDataForSelection"
			class="h-100"
			icon="fa-duotone fa-cloud-binary"
			title="No data for this configuration"
			text="No data available for the period selection and/or selected filters"
		/>

		<v-empty-state
			v-else-if="!isLoading && unableToLoadData"
			class="h-100"
			icon="fa-duotone fa-wifi-exclamation"
			title="Unable to load graph data"
			text="If the issue persists, you may need to update the configuration."
		/>

		<v-empty-state
			v-else-if="!isLoading && tooMuchDataForSelection"
			class="h-100"
			icon="fa-duotone fa-wifi-exclamation"
			text="Unable to render configuration as it would be too large to visualize"
			title="Unable to visualize data"
		/>
	</div>
</template>

<style lang="scss">
.position-canvas-absolute > canvas {
	position: absolute;
}
</style>
